import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useDataRefresh } from "@/hooks/useDataRefresh";
import { toast } from "sonner";
import {
  Loader2,
  FileText,
  Download,
  Sparkles,
  AlertTriangle,
  RefreshCw,
  Trash2,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  X,
  PenLine,
  Send,
  Copy,
  Link2,
  CloudUpload,
} from "lucide-react";

/**
 * DGT Documents Dialog — Mod.04 / Mod.07 / Mod.12.
 *
 * Backend endpoints:
 *   GET  /api/dgt/modules
 *   GET  /api/students/{id}/dgt/{mod}/preview
 *   POST /api/students/{id}/dgt/{mod}/generate   { overrides }
 *   GET  /api/students/{id}/dgt/documents
 *   GET  /api/dgt/documents/{id}/pdf              (inline download)
 *   DELETE /api/dgt/documents/{id}                (sudo-gated)
 *
 * Flow:
 *   1. Pick module (Mod.04 / Mod.07 / Mod.12).
 *   2. Preview: show the projected field values from Ficha Maestra + settings,
 *      highlight `missing_fields` in an editable "overrides" form.
 *   3. Generate: creates the filled PDF, appends to the student's DGT history.
 *   4. Actions: download + (Mod.04/07) request remote signature (Bloque B stub).
 */
const MOD_META = {
  mod04: {
    label: "Mod.04",
    title: "Solicitud de Pruebas de Aptitud",
    needsSignature: true,
    color: "text-orange-700 bg-orange-50 border-orange-200",
  },
  mod07: {
    label: "Mod.07",
    title: "Traslado / Anulación de Citación",
    needsSignature: true,
    color: "text-blue-700 bg-blue-50 border-blue-200",
  },
  mod12: {
    label: "Mod.12",
    title: "Ficha de Prácticas (uso interno)",
    needsSignature: false,
    color: "text-emerald-700 bg-emerald-50 border-emerald-200",
  },
};

// User-facing label for the raw AcroForm field name coming from the DGT PDF.
// Only overrides commonly-needed fields; other names show as-is.
const OVERRIDE_LABEL = {
  dni: "DNI/NIE del alumno",
  nombre: "Nombre",
  apellido1: "Primer apellido",
  apellido2: "Segundo apellido",
  fecha_nac: "Fecha nacimiento (DD/MM/AAAA)",
  telefono: "Teléfono",
  email: "Email",
  tipo_via: "Tipo de vía",
  nombre_via: "Nombre de la vía",
  numero_via: "Número",
  bloque: "Bloque",
  portal: "Portal",
  escalera: "Escalera",
  planta: "Planta",
  puerta: "Puerta",
  codigo_postal: "Código postal",
  localidad: "Localidad",
  provincia: "Provincia",
  permiso: "Permiso solicitado",
  sexo: "Sexo (hombre/mujer)",
  transmision: "Transmisión (manual/automatica)",
  tramite: "Trámite que desea realizar",
  solicitud: "Tipo (traslado/anulacion)",
  causas: "Causas",
  justificacion: "Justificación",
  origen_denominacion: "Autoescuela origen — denominación",
  origen_numero: "Autoescuela origen — nº DGT",
  origen_seccion: "Autoescuela origen — sección",
  origen_digito: "Autoescuela origen — dígito control",
  fecha_examen: "Fecha del examen (anulación)",
  profesor_nombre: "Profesor asignado",
  practica: "Tipo de práctica (circulacion/maniobras)",
  observaciones: "Observaciones",
  tasa: "TASA (nº de tasa DGT)",
  centro_reconocimiento: "Centro de reconocimiento",
  lentes: "Lentes",
  cond_restrictivas: "Condiciones restrictivas",
  validez_limitada: "Validez limitada",
};

// Which override keys are surfaced by default for a given module,
// on top of any missing critical fields the backend reports.
const OVERRIDE_HINTS = {
  mod04: ["dni", "nombre", "apellido1", "apellido2", "fecha_nac", "sexo",
          "tipo_via", "nombre_via", "numero_via",
          "codigo_postal", "localidad", "provincia", "telefono", "email",
          "permiso", "tramite", "transmision",
          "tasa", "centro_reconocimiento", "lentes", "cond_restrictivas", "validez_limitada"],
  mod07: ["dni", "nombre", "apellido1", "solicitud", "causas", "justificacion",
          "permiso", "origen_denominacion", "origen_numero", "origen_seccion",
          "origen_digito", "fecha_examen"],
  mod12: ["dni", "nombre", "apellido1", "apellido2", "profesor_nombre",
          "practica", "permiso", "observaciones"],
};

// Map a critical AcroForm field name back to a logical override key.
const CRITICAL_TO_OVERRIDE = {
  "DNINIE": "dni",
  "DNINIE_2": "dni",
  "Nombre": "nombre",
  "Apellido 1": "apellido1",
  "Apellidos": "apellido1",   // Mod07 combines apellidos
  "Fecha nacimiento": "fecha_nac",
  "Tipo Vía": "tipo_via",
  "Nombre de la vía": "nombre_via",
  "Código Postal": "codigo_postal",
  "Localidad": "localidad",
  "teléfono": "telefono",
  "Correo electrónico": "email",
  "Clase de permisoAutorización": "permiso",
  "Permiso que solicita": "permiso",
  "Profesor": "profesor_nombre",
};

// V1.25.5 · Inverse map: override key → priority-ordered list of AcroForm
// field names that hold the corresponding Ficha Maestra value. Used to
// prefill the visible form so the user always SEES exactly which value will
// be written to the PDF (evita el patrón "vacío = usar Ficha Maestra").
const OVERRIDE_TO_ACROFORM = {
  dni:                  ["DNINIE", "DNINIE_2"],
  nombre:               ["Nombre"],
  apellido1:            ["Apellido 1", "Apellidos"],
  apellido2:            ["Apellido 2"],
  fecha_nac:            ["Fecha nacimiento"],
  telefono:             ["teléfono", "Teléfono"],
  email:                ["Correo electrónico"],
  tipo_via:             ["Tipo Vía", "Tipo vía"],
  nombre_via:           ["Nombre de la vía"],
  numero_via:           ["Número", "Nº"],
  bloque:               ["Bloque"],
  portal:               ["Portal"],
  escalera:             ["Escalera"],
  planta:               ["Planta"],
  puerta:               ["Puerta"],
  codigo_postal:        ["Código Postal"],
  localidad:            ["Localidad"],
  provincia:            ["Provincia"],
  permiso:              ["Clase de permisoAutorización", "Permiso que solicita", "Permiso"],
  sexo:                 ["Sexo", "Hombre", "Mujer"],
  transmision:          ["Transmisión", "Manual", "Automática", "transmision"],
  tramite:              ["tramite"],
  solicitud:            ["Solicitud", "Traslado", "Anulación"],
  causas:               ["Causas"],
  justificacion:        ["Justificación"],
  origen_denominacion:  ["Denominación"],
  origen_numero:        ["Nº autoescuela"],
  origen_seccion:       ["Sección"],
  origen_digito:        ["Dígito control", "Dígito"],
  fecha_examen:         ["Fecha examen"],
  profesor_nombre:      ["Profesor"],
  practica:             ["Práctica", "Circulación", "Maniobras"],
  observaciones:        ["Observaciones"],
  tasa:                 ["Tasa1"],
  centro_reconocimiento: ["Centro de reconocimiento"],
  lentes:               ["lentes"],
  cond_restrictivas:    ["condRestrictivas"],
  validez_limitada:     ["ValLimit"],
};

/**
 * Resolve the current Ficha Maestra value for a given override key by
 * looking up `preview.values` in the priority order defined above.
 * Returns the first non-empty match, or "" if none.
 */
function resolveFromPreview(overrideKey, previewValues) {
  const candidates = OVERRIDE_TO_ACROFORM[overrideKey] || [];
  for (const acro of candidates) {
    const v = previewValues?.[acro];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return String(v);
    }
  }
  return "";
}

/**
 * Compute the full override prefill (surfaced hints + backend-flagged missing
 * critical fields) hydrated with the ACTUAL current values from Ficha Maestra.
 */
function buildFichaPrefill(mod, previewData) {
  const previewValues = previewData?.values || {};
  const missing = previewData?.missing_fields || [];
  const surfaced = new Set(OVERRIDE_HINTS[mod] || []);
  missing.forEach((f) => {
    const k = CRITICAL_TO_OVERRIDE[f];
    if (k) surfaced.add(k);
  });
  const out = {};
  surfaced.forEach((k) => {
    out[k] = resolveFromPreview(k, previewValues);
  });
  return out;
}


function StatusBadge({ status }) {
  const map = {
    draft: { label: "Borrador", cls: "bg-neutral-100 text-neutral-700" },
    awaiting_signature: { label: "Firma pendiente", cls: "bg-amber-100 text-amber-800" },
    signed: { label: "Firmado", cls: "bg-emerald-100 text-emerald-800" },
    cancelled: { label: "Cancelado", cls: "bg-red-100 text-red-700" },
  };
  const s = map[status] || map.draft;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
}


export default function DgtDocumentsDialog({ open, student, onClose, embedded = false }) {
  const [tab, setTab] = useState("list");   // list | generator
  const [mod, setMod] = useState("mod04");
  const [loadingList, setLoadingList] = useState(false);
  const [docs, setDocs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [overrides, setOverrides] = useState({});
  // V1.26.1 · Lista de profesores autorizados para el desplegable del Mod.12.
  const [professors, setProfessors] = useState([]);
  const [selectedProfId, setSelectedProfId] = useState("");
  // V1.55 · Mod.04 — centros de reconocimiento + TASA automática
  const [centers, setCenters] = useState([]);

  const studentId = student?.id;

  const reload = useCallback(async () => {
    if (!studentId) return;
    setLoadingList(true);
    try {
      const { data } = await api.get(`/students/${studentId}/dgt/documents`);
      setDocs(data || []);
    } catch (e) {
      toast.error(formatApiError(e, "No se han podido cargar los documentos DGT"));
    } finally {
      setLoadingList(false);
    }
  }, [studentId]);
  useDataRefresh(open || embedded ? reload : null);

  useEffect(() => {
    if ((open || embedded) && studentId) {
      reload();
      setTab("list");
      setPreview(null);
      setOverrides({});
      setSelectedProfId("");
      // V1.26.1 · Cargamos profesores para tenerlos listos cuando se abra Mod.12.
      // V1.38 · Fuente única = `/teachers` (Profesorado UI). Deprecado `/professors`.
      api.get("/teachers", { params: { include_inactive: false } })
        .then(({ data }) => setProfessors(data || []))
        .catch(() => setProfessors([]));
    }
  }, [open, embedded, studentId, reload]);

  // V1.55 · Mod.04 — carga centros de reconocimiento y prefill de TASA
  // (la tasa disponible del alumno se añade automáticamente a la solicitud).
  useEffect(() => {
    if (tab !== "generator" || mod !== "mod04" || !studentId || !preview) return;
    api.get("/recognition-centers")
      .then(({ data }) => setCenters(data || []))
      .catch(() => setCenters([]));
    api.get(`/students/${studentId}/dgt-tasas`)
      .then(({ data }) => {
        const t = (data || []).find((x) => x?.consumption?.estado === "disponible") || (data || [])[0];
        if (t?.number) setOverrides((o) => (o.tasa ? o : { ...o, tasa: t.number }));
      })
      .catch(() => {});
  }, [tab, mod, studentId, preview]);

  const runPreview = useCallback(async (nextMod) => {
    if (!studentId) return;
    setPreviewing(true);
    try {
      const { data } = await api.get(`/students/${studentId}/dgt/${nextMod}/preview`);
      // V1.25.5 · Guardia de integridad — abortar si el backend devuelve un
      // student_id distinto al solicitado. Nunca fallback silencioso.
      if (data?.student_id && data.student_id !== studentId) {
        console.error(
          "[data-integrity][dgt] preview student_id mismatch",
          { requested: studentId, received: data.student_id },
        );
        toast.error("Se ha detectado un cruce de identidad. Operación abortada.");
        setPreview(null);
        setOverrides({});
        return;
      }
      setPreview(data);
      // V1.25.5 · Prefill con VALORES REALES de la Ficha Maestra (visibles al
      // usuario). Antes se dejaban vacíos y el backend rellenaba en oscuras.
      setOverrides(buildFichaPrefill(nextMod, data));
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo generar la previsualización"));
    } finally {
      setPreviewing(false);
    }
  }, [studentId]);

  /**
   * V1.25.5 · Recarga los datos actuales de la Ficha Maestra en el formulario
   * visible, sobreescribiendo cualquier edición manual.
   */
  const refillFromFicha = useCallback(async () => {
    if (!studentId || !mod) return;
    setPreviewing(true);
    try {
      const { data } = await api.get(`/students/${studentId}/dgt/${mod}/preview`);
      if (data?.student_id && data.student_id !== studentId) {
        console.error(
          "[data-integrity][dgt] refill student_id mismatch",
          { requested: studentId, received: data.student_id },
        );
        toast.error("Se ha detectado un cruce de identidad. Operación abortada.");
        return;
      }
      setPreview(data);
      setOverrides(buildFichaPrefill(mod, data));
      toast.success("Datos recargados desde la Ficha Maestra");
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo recargar la Ficha Maestra"));
    } finally {
      setPreviewing(false);
    }
  }, [studentId, mod]);

  const startGenerator = useCallback(async (nextMod) => {
    setMod(nextMod);
    setTab("generator");
    await runPreview(nextMod);
  }, [runPreview]);

  const doGenerate = useCallback(async () => {
    if (!studentId) return;
    // V1.25.5 · Validación de integridad — no permitir generar si el preview
    // cargado pertenece a otro alumno.
    if (preview?.student_id && preview.student_id !== studentId) {
      console.error(
        "[data-integrity][dgt] generate blocked — preview belongs to another student",
        { requested: studentId, preview_student_id: preview.student_id },
      );
      toast.error(
        "Ficha Maestra no coincide con el alumno actual. Generación bloqueada.",
      );
      return;
    }
    setGenerating(true);
    try {
      // V1.55 · Centro de reconocimiento manual — si no existe en el
      // repositorio maestro, se registra automáticamente sólo con su código
      // para que la administración complete el resto después.
      const manualCode = (overrides.centro_reconocimiento || "").trim();
      if (mod === "mod04" && manualCode && !overrides._centro_sel && manualCode.length <= 20) {
        const exists = centers.some((c) =>
          (c.recognition_center_number || "") === manualCode ||
          `${c.recognition_center_number || ""} ${c.name || ""}`.trim() === manualCode
        );
        if (!exists) {
          try {
            await api.post("/recognition-centers", {
              name: `(Pendiente de completar) · Código ${manualCode}`,
              recognition_center_number: manualCode,
            });
          } catch { /* no bloquea la generación */ }
        }
      }
      // V1.25.5 · Enviar los overrides no vacíos. El formulario ya muestra
      // los valores reales de la Ficha Maestra prefill-eados de forma visible;
      // el usuario puede editarlos o borrarlos. Si un campo queda vacío se
      // omite del payload y el backend aplicará su propio default de Ficha
      // Maestra (comportamiento seguro — nunca escribe cadena vacía forzada).
      const cleaned = Object.fromEntries(
        Object.entries(overrides).filter(([, v]) => (v ?? "").toString().trim() !== "")
      );
      const { data } = await api.post(
        `/students/${studentId}/dgt/${mod}/generate`,
        { overrides: cleaned },
      );
      // Guardia post-respuesta: nunca deberia ocurrir, pero blindaje extra.
      if (data?.student_id && data.student_id !== studentId) {
        console.error(
          "[data-integrity][dgt] generated doc has mismatched student_id",
          { requested: studentId, received: data.student_id },
        );
        toast.error("Inconsistencia crítica detectada — se abre el PDF para revisión pero NO se recarga la lista.");
        return;
      }
      toast.success(`Documento ${MOD_META[mod].label} generado`);
      // Refresh & switch to list
      await reload();
      setTab("list");
      setPreview(null);
      setOverrides({});
      // Open PDF in a new tab
      openPdf(data.id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "missing_fields") {
        toast.error(
          `Faltan datos: ${detail.missing_fields.join(", ")}`,
          { description: "Rellena los campos marcados y vuelve a intentarlo." },
        );
        // refresh preview to highlight
        await runPreview(mod);
      } else {
        toast.error(formatApiError(e, "No se pudo generar el documento DGT"));
      }
    } finally {
      setGenerating(false);
    }
  }, [studentId, mod, overrides, preview, reload, runPreview]);

  const [driveUploading, setDriveUploading] = useState("");
  const uploadToOneDrive = useCallback(async (docId) => {
    setDriveUploading(docId);
    try {
      const { data } = await api.post(`/dgt/documents/${docId}/upload-onedrive`);
      toast.success(data.deduplicated
        ? "Ya estaba en OneDrive (misma versión) — nada que subir."
        : "Documento subido a OneDrive (carpeta DGT).");
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo subir a OneDrive"));
    } finally {
      setDriveUploading("");
    }
  }, []);

  const openPdf = useCallback((docId) => {
    // Build absolute URL so it opens in a new window with the auth token
    // The backend expects Bearer token — since window.open cannot inject
    // headers, we rely on the fetch-and-blob pattern.
    (async () => {
      try {
        const res = await api.get(`/dgt/documents/${docId}/pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        window.open(url, "_blank", "noopener,noreferrer");
        // Revoke after a while
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        toast.error(formatApiError(e, "No se pudo abrir el PDF"));
      }
    })();
  }, []);

  const deleteDoc = useCallback(async (docId) => {
    if (!window.confirm("¿Eliminar este documento DGT? (requiere PIN maestro)")) return;
    try {
      await api.delete(`/dgt/documents/${docId}`);
      toast.success("Documento eliminado");
      reload();
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo eliminar el documento"));
    }
  }, [reload]);

  // ------- Signature request (Mod.04/07 remote signing) -------
  const [sigRequesting, setSigRequesting] = useState(false);
  const requestSignature = useCallback(async (doc) => {
    if (!student || sigRequesting) return;
    const signerName =
      (student.name || "").trim() +
      (student.primer_apellido ? " " + student.primer_apellido.trim() : "");
    const phoneRaw = (student.phone || "").replace(/\D+/g, "");
    const phone = phoneRaw.startsWith("34") || phoneRaw.length <= 9
      ? phoneRaw
      : phoneRaw; // best-effort — keep as-is
    if (!phoneRaw) {
      toast.error("El alumno no tiene teléfono registrado. Añádelo antes de solicitar firma.");
      return;
    }
    setSigRequesting(true);
    try {
      const { data } = await api.post(
        `/dgt/documents/${doc.id}/request-signature`,
        {
          signer_name: signerName || "Alumno",
          signer_dni: student.dni || "",
          signer_phone: student.phone || "",
          signer_email: student.email || "",
          require_otp: true,
        },
      );
      const portal = data.portal_url || `${window.location.origin}/firmar-dgt/${data.token}`;
      const codigo = student.codigo_expediente ? ` · Expediente ${student.codigo_expediente}` : "";
      const nombre = (student.name || "").split(" ")[0] || "";
      const modLabel = MOD_META[doc.mod].label;
      const msg =
        `Hola ${nombre}, Natur Autoescuela necesita tu firma para el documento oficial ${modLabel} (${MOD_META[doc.mod].title}).${codigo}\n\n` +
        `Ábrelo aquí y firma con tu móvil: ${portal}\n\n` +
        `Recibirás un código de verificación al pulsar "Enviar código" en el enlace. Este proceso solo lleva 1 minuto.`;
      const waPhone = phoneRaw.startsWith("34") ? phoneRaw : (phoneRaw.length === 9 ? "34" + phoneRaw : phoneRaw);
      const wa = `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`;
      // Copy to clipboard AND open WhatsApp
      try {
        await navigator.clipboard.writeText(portal);
      } catch { /* silent */ }
      toast.success("Enlace de firma copiado. Abriendo WhatsApp…", {
        description: "El alumno debe verificar OTP antes de firmar.",
      });
      window.open(wa, "_blank", "noopener,noreferrer");
      reload();
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo solicitar la firma"));
    } finally {
      setSigRequesting(false);
    }
  }, [student, sigRequesting, reload]);

  const copySignatureLink = useCallback(async (doc) => {
    if (!doc.signature_token) return;
    const portal = `${window.location.origin}/firmar-dgt/${doc.signature_token}`;
    try {
      await navigator.clipboard.writeText(portal);
      toast.success("Enlace copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar el enlace");
    }
  }, []);

  const overrideKeys = useMemo(() => Object.keys(overrides), [overrides]);

  if (!student) return null;

  const content = (
    <>
        {tab === "list" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {["mod04", "mod07", "mod12"].map((m) => (
                <button
                  key={m}
                  data-testid={`dgt-mod-${m}`}
                  onClick={() => startGenerator(m)}
                  className={`text-left rounded-xl border p-3 transition-shadow hover:shadow-md ${MOD_META[m].color}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold text-lg">{MOD_META[m].label}</span>
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <p className="text-xs mt-1 leading-tight">{MOD_META[m].title}</p>
                  {MOD_META[m].needsSignature ? (
                    <p className="text-[10px] mt-2 opacity-75 flex items-center gap-1">
                      <PenLine className="h-3 w-3" /> Firma remota WhatsApp
                    </p>
                  ) : (
                    <p className="text-[10px] mt-2 opacity-75 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Uso interno (firmas auto)
                    </p>
                  )}
                </button>
              ))}
            </div>

            <div className="border-t border-neutral-200 pt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-sm">Historial ({docs.length})</h3>
                <button
                  onClick={reload}
                  className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900"
                  data-testid="dgt-refresh"
                >
                  <RefreshCw className="h-3 w-3" /> Refrescar
                </button>
              </div>

              {loadingList ? (
                <div className="text-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-neutral-500 mx-auto" />
                </div>
              ) : docs.length === 0 ? (
                <p className="text-sm text-neutral-500 py-4 text-center">
                  Sin documentos generados todavía.
                </p>
              ) : (
                <ul className="space-y-2">
                  {docs.map((d) => (
                    <li
                      key={d.id}
                      data-testid={`dgt-doc-${d.id}`}
                      className="flex items-center justify-between gap-2 rounded-lg border border-neutral-200 bg-white p-2.5 hover:bg-neutral-50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-neutral-800">
                            {MOD_META[d.mod]?.label || d.mod}
                          </span>
                          <StatusBadge status={d.status} />
                          <span className="text-xs text-neutral-500 truncate">
                            {MOD_META[d.mod]?.title}
                          </span>
                        </div>
                        <p className="text-[11px] text-neutral-500 mt-0.5">
                          {new Date(d.created_at).toLocaleString("es-ES")} · {d.created_by}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => openPdf(d.id)}
                          data-testid={`dgt-download-${d.id}`}
                          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-neutral-700 hover:bg-neutral-100"
                          title="Abrir PDF"
                        >
                          <Download className="h-3.5 w-3.5" /> Abrir
                        </button>
                        <button
                          onClick={() => uploadToOneDrive(d.id)}
                          disabled={driveUploading === d.id}
                          data-testid={`dgt-upload-onedrive-${d.id}`}
                          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-sky-700 hover:bg-sky-50 disabled:opacity-50"
                          title="Subir a OneDrive (carpeta DGT del expediente)"
                        >
                          <CloudUpload className="h-3.5 w-3.5" /> OneDrive
                        </button>
                        {MOD_META[d.mod]?.needsSignature && d.status === "draft" && (
                          <button
                            onClick={() => requestSignature(d)}
                            disabled={sigRequesting}
                            data-testid={`dgt-request-sig-${d.id}`}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-50"
                            title="Solicitar firma vía WhatsApp"
                          >
                            <Send className="h-3.5 w-3.5" /> Solicitar firma
                          </button>
                        )}
                        {d.status === "awaiting_signature" && d.signature_token && (
                          <button
                            onClick={() => copySignatureLink(d)}
                            data-testid={`dgt-copy-siglink-${d.id}`}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200"
                            title="Copiar enlace de firma"
                          >
                            <Link2 className="h-3.5 w-3.5" /> Copiar enlace
                          </button>
                        )}
                        <button
                          onClick={() => deleteDoc(d.id)}
                          data-testid={`dgt-delete-${d.id}`}
                          className="inline-flex items-center h-8 px-2 rounded-md text-red-600 hover:bg-red-50"
                          title="Eliminar"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === "generator" && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setTab("list"); setPreview(null); }}
                data-testid="dgt-back-to-list"
                className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900"
              >
                <ChevronLeft className="h-3 w-3" /> Volver al historial
              </button>
            </div>

            <div className={`rounded-xl border p-3 ${MOD_META[mod].color}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-lg">{MOD_META[mod].label}</span>
                <span className="text-sm">{MOD_META[mod].title}</span>
              </div>
            </div>

            {previewing ? (
              <div className="py-10 text-center">
                <Loader2 className="h-6 w-6 animate-spin text-neutral-500 mx-auto" />
                <p className="text-xs text-neutral-500 mt-2">Analizando Ficha Maestra…</p>
              </div>
            ) : preview ? (
              <>
                {/* V1.25.5 · Cabecera obligatoria de origen de datos.
                    Deja explícito de qué Ficha Maestra provienen los valores
                    para facilitar la detección de cruces de identidad. */}
                <div
                  className="flex items-start gap-2 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-emerald-900 text-sm"
                  data-testid="dgt-ficha-source-banner"
                >
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-800">
                      Datos cargados desde la Ficha Maestra de:
                    </p>
                    <p
                      className="font-semibold text-emerald-950 text-sm mt-0.5"
                      data-testid="dgt-ficha-student-name"
                    >
                      {[student.name, student.primer_apellido, student.segundo_apellido]
                        .filter(Boolean)
                        .join(" ")}
                      {student.dni && (
                        <span className="ml-2 font-mono text-xs text-emerald-800">
                          · {student.dni}
                        </span>
                      )}
                      {student.codigo_expediente && (
                        <span className="ml-2 inline-flex items-center rounded bg-neutral-900 text-white px-1.5 py-0.5 text-[10px] font-bold font-mono">
                          {student.codigo_expediente}
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-emerald-800 mt-1">
                      Puedes editarlos antes de generar. Al pulsar
                      <em> Rellenar desde Ficha Maestra</em> se recargan los
                      valores actuales del alumno, sobrescribiendo cambios.
                    </p>
                  </div>
                  {/* V1.25.5 · Bandera de integridad de student_id */}
                  {preview.student_id !== studentId && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-[11px] font-semibold border border-red-300"
                      data-testid="dgt-integrity-warning"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      Cruce de identidad detectado
                    </span>
                  )}
                </div>

                {/* Missing fields warning */}
                {!preview.ready && (
                  <div
                    className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900 text-sm"
                    data-testid="dgt-missing-warning"
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Faltan datos obligatorios:</p>
                      <p className="text-xs mt-1">
                        {preview.missing_fields.join(", ")}
                      </p>
                    </div>
                  </div>
                )}

                {/* V1.25.6 · Auditoría de la Ficha Maestra
                    Muestra qué campos personales del alumno están vacíos en
                    la propia Ficha Maestra (no en el override). Explica al
                    usuario que si el PDF va a salir con campos vacíos, es
                    porque la Ficha Maestra tampoco los tiene. */}
                {preview.ficha_audit && preview.ficha_audit.empty?.length > 0 && (
                  <div
                    className="rounded-lg border border-neutral-300 bg-neutral-50 p-3 text-sm text-neutral-800"
                    data-testid="dgt-ficha-audit-panel"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide font-bold text-neutral-600">
                        Auditoría · Ficha Maestra del alumno
                      </p>
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-neutral-900 text-white px-2 py-0.5 text-[10px] font-bold"
                        data-testid="dgt-ficha-completeness"
                      >
                        {preview.ficha_audit.completeness_pct}% completa
                      </span>
                    </div>
                    <p className="text-[11px] text-neutral-600 mt-1">
                      Los siguientes campos <strong>NO están guardados en la
                      Ficha Maestra</strong> — si los dejas vacíos también en
                      el formulario de abajo, se escribirán en blanco en el
                      PDF. Rellena la ficha del alumno para que el
                      autocompletado funcione siempre.
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {preview.ficha_audit.empty.map((k) => (
                        <span
                          key={k}
                          data-testid={`dgt-ficha-empty-${k}`}
                          className="inline-flex items-center rounded bg-white border border-neutral-300 text-neutral-700 px-1.5 py-0.5 text-[10px] font-mono"
                        >
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Overrides form */}
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-neutral-800">
                      Datos que se escribirán en el PDF
                    </h4>
                    <button
                      type="button"
                      onClick={refillFromFicha}
                      disabled={previewing}
                      data-testid="dgt-refill-from-ficha"
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full border border-emerald-300 bg-white text-emerald-800 text-[11px] font-semibold hover:bg-emerald-50 disabled:opacity-50"
                    >
                      <RefreshCw className={`h-3 w-3 ${previewing ? "animate-spin" : ""}`} />
                      Rellenar desde Ficha Maestra
                    </button>
                  </div>
                  <p className="text-[11px] text-neutral-500">
                    Todos los datos son editables. Los campos que dejes vacíos
                    NO se escribirán en el documento (deben rellenarse
                    explícitamente).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {overrideKeys.map((k) => {
                      // V1.26.1 · Campo profesor_nombre en Mod.12 pasa a ser
                      // un desplegable alimentado por la tabla de profesores.
                      // El nombre y DNI llegan al PDF DESDE LA TABLA — nunca
                      // como texto libre.
                      if (mod === "mod12" && k === "profesor_nombre") {
                        return (
                          <label key={k} className="flex flex-col gap-1 sm:col-span-2">
                            <span className="text-[11px] font-semibold text-neutral-700">
                              Profesor asignado (obligatorio)
                            </span>
                            <select
                              value={selectedProfId}
                              onChange={(e) => {
                                const pid = e.target.value;
                                setSelectedProfId(pid);
                                const prof = professors.find((p) => p.id === pid);
                                setOverrides({
                                  ...overrides,
                                  profesor_id: pid,
                                  profesor_nombre: prof ? prof.name : "",
                                  profesor_dni: prof ? prof.dni : "",
                                });
                              }}
                              data-testid="dgt-override-profesor-select"
                              className="h-8 px-2 rounded border border-neutral-300 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                            >
                              <option value="">— Selecciona un profesor —</option>
                              {professors.map((p) => (
                                <option key={p.id} value={p.id} data-testid={`dgt-prof-opt-${p.id}`}>
                                  {p.name} · {p.dni}
                                </option>
                              ))}
                            </select>
                            {selectedProfId && (
                              <span className="text-[10px] text-emerald-700 font-mono">
                                DNI del profesor: {overrides.profesor_dni || ""}
                              </span>
                            )}
                            {professors.length === 0 && (
                              <span className="text-[10px] text-amber-700">
                                No hay profesores registrados. Añádelos desde Configuración.
                              </span>
                            )}
                          </label>
                        );
                      }
                      // profesor_dni no se muestra como campo aparte —
                      // se resuelve automáticamente al elegir profesor.
                      if (k === "profesor_dni") return null;
                      if (k === "_centro_sel") return null;
                      // V1.55 · Mod.04 — Sexo como checkbox Hombre/Mujer
                      if (mod === "mod04" && k === "sexo") {
                        const cur = (overrides.sexo || "").toLowerCase();
                        return (
                          <label key={k} className="flex flex-col gap-1">
                            <span className="text-[11px] font-semibold text-neutral-700">Sexo</span>
                            <div className="flex gap-2">
                              {[["hombre", "Hombre"], ["mujer", "Mujer"]].map(([v, lbl]) => (
                                <button key={v} type="button" onClick={() => setOverrides({ ...overrides, sexo: v })}
                                  data-testid={`dgt-sexo-${v}`}
                                  className={"flex-1 h-8 rounded border text-xs font-semibold transition-colors " + (cur === v ? "border-orange-400 bg-orange-50 text-orange-800" : "border-neutral-300 bg-white text-neutral-600")}>
                                  {cur === v ? "☒ " : "☐ "}{lbl}
                                </button>
                              ))}
                            </div>
                          </label>
                        );
                      }
                      // V1.57.2 · Mod.04 — Trámite y Transmisión como selectores
                      if (mod === "mod04" && k === "tramite") {
                        const cur = (overrides.tramite || "permiso").toLowerCase();
                        return (
                          <label key={k} className="flex flex-col gap-1 sm:col-span-2">
                            <span className="text-[11px] font-semibold text-neutral-700">Trámite que desea realizar</span>
                            <div className="flex gap-2">
                              {[["permiso", "Permiso/Licencia de conducción"], ["autorizacion", "Autorización MMPP"], ["rpv", "RPV"]].map(([v, lbl]) => (
                                <button key={v} type="button" onClick={() => setOverrides({ ...overrides, tramite: v })}
                                  data-testid={`dgt-tramite-${v}`}
                                  className={"flex-1 h-8 rounded border text-xs font-semibold transition-colors " + (cur === v ? "border-orange-400 bg-orange-50 text-orange-800" : "border-neutral-300 bg-white text-neutral-600")}>
                                  {cur === v ? "☒ " : "☐ "}{lbl}
                                </button>
                              ))}
                            </div>
                          </label>
                        );
                      }
                      if (mod === "mod04" && k === "transmision") {
                        const cur = (overrides.transmision || "manual").toLowerCase();
                        return (
                          <label key={k} className="flex flex-col gap-1">
                            <span className="text-[11px] font-semibold text-neutral-700">Transmisión</span>
                            <div className="flex gap-2">
                              {[["manual", "Manual"], ["automatica", "Automática"]].map(([v, lbl]) => (
                                <button key={v} type="button" onClick={() => setOverrides({ ...overrides, transmision: v })}
                                  data-testid={`dgt-transmision-${v}`}
                                  className={"flex-1 h-8 rounded border text-xs font-semibold transition-colors " + (cur === v ? "border-orange-400 bg-orange-50 text-orange-800" : "border-neutral-300 bg-white text-neutral-600")}>
                                  {cur === v ? "☒ " : "☐ "}{lbl}
                                </button>
                              ))}
                            </div>
                          </label>
                        );
                      }
                      // V1.55 · Mod.04 — Centro de reconocimiento: lista o código manual
                      if (mod === "mod04" && k === "centro_reconocimiento") {
                        return (
                          <label key={k} className="flex flex-col gap-1 sm:col-span-2">
                            <span className="text-[11px] font-semibold text-neutral-700">Centro de reconocimiento</span>
                            <select
                              value={overrides._centro_sel || ""}
                              onChange={(e) => {
                                const cid = e.target.value;
                                const c = centers.find((x) => x.id === cid);
                                setOverrides({
                                  ...overrides,
                                  _centro_sel: cid,
                                  centro_reconocimiento: c ? `${c.recognition_center_number || ""} ${c.name}`.trim() : "",
                                });
                              }}
                              data-testid="dgt-centro-select"
                              className="h-8 px-2 rounded border border-neutral-300 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                            >
                              <option value="">— No está en la lista / código manual —</option>
                              {centers.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}{c.recognition_center_number ? ` · ${c.recognition_center_number}` : ""}</option>
                              ))}
                            </select>
                            {!overrides._centro_sel && (
                              <input
                                type="text"
                                value={overrides.centro_reconocimiento || ""}
                                onChange={(e) => setOverrides({ ...overrides, centro_reconocimiento: e.target.value })}
                                placeholder="Código del centro — se registrará automáticamente en el maestro"
                                data-testid="dgt-centro-manual"
                                className="h-8 px-2 rounded border border-neutral-300 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                              />
                            )}
                          </label>
                        );
                      }
                      // V1.55 · Mod.04 — checks de la banda de reconocimiento
                      if (mod === "mod04" && ["lentes", "cond_restrictivas", "validez_limitada"].includes(k)) {
                        const lbl = { lentes: "Lentes", cond_restrictivas: "Condiciones restrictivas", validez_limitada: "Validez limitada" }[k];
                        return (
                          <label key={k} className="flex items-center gap-2 text-sm self-end h-8">
                            <input type="checkbox" checked={overrides[k] === "x"}
                              onChange={(e) => setOverrides({ ...overrides, [k]: e.target.checked ? "x" : "" })}
                              data-testid={`dgt-override-${k}`} className="h-4 w-4 accent-orange-500" />
                            {lbl}
                          </label>
                        );
                      }
                      return (
                        <label key={k} className="flex flex-col gap-1">
                          <span className="text-[11px] font-semibold text-neutral-700">
                            {OVERRIDE_LABEL[k] || k}
                          </span>
                          <input
                            type="text"
                            value={overrides[k] || ""}
                            onChange={(e) => setOverrides({ ...overrides, [k]: e.target.value })}
                            data-testid={`dgt-override-${k}`}
                            className="h-8 px-2 rounded border border-neutral-300 bg-white text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Preview values (read-only, condensed) */}
                <details className="rounded-lg border border-neutral-200 bg-white p-3">
                  <summary className="cursor-pointer text-xs font-semibold text-neutral-700">
                    Ver todos los datos que se rellenarán ({Object.keys(preview.values).length})
                  </summary>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-[11px] font-mono max-h-72 overflow-y-auto">
                    {Object.entries(preview.values).map(([k, v]) => (
                      <div key={k} className="flex gap-1 border-b border-neutral-100 py-0.5">
                        <span className="text-neutral-500 truncate flex-shrink-0" style={{ width: 140 }} title={k}>{k}</span>
                        <span className={v ? "text-neutral-900" : "text-neutral-300 italic"}>
                          {v || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </details>

                <div className="flex items-center justify-between gap-3 pt-2">
                  <button
                    onClick={() => runPreview(mod)}
                    className="inline-flex items-center gap-1 text-xs text-neutral-600 hover:text-neutral-900"
                    data-testid="dgt-refresh-preview"
                  >
                    <RefreshCw className="h-3 w-3" /> Refrescar Ficha Maestra
                  </button>
                  <button
                    onClick={doGenerate}
                    disabled={generating || preview.student_id !== studentId}
                    data-testid="dgt-generate-btn"
                    className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {generating ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Generando…</>
                    ) : (
                      <><Sparkles className="h-4 w-4" /> Generar {MOD_META[mod].label} <ChevronRight className="h-4 w-4" /></>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <p className="text-sm text-neutral-500 py-6 text-center">Sin previsualización.</p>
            )}
          </div>
        )}
      </>
  );

  if (embedded) {
    return <div className="space-y-4" data-testid="dgt-documents-embedded">{content}</div>;
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="dgt-documents-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <FileText className="h-5 w-5 text-neutral-700" />
            Documentación DGT — {student.name}
            {student.codigo_expediente && (
              <span className="inline-flex items-center rounded-md bg-neutral-900 text-white px-2 py-0.5 text-xs font-bold font-mono tracking-wider">
                {student.codigo_expediente}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Rellena las plantillas oficiales de la DGT usando la Ficha Maestra
            del alumno. Los datos corporativos de la autoescuela se autocompletan.
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
